"use client";

import { useEffect } from "react";

// Removed global gtag declaration to avoid conflicts with existing declarations

// Core Web Vitals interface matching the latest web-vitals library
interface Metric {
  name: string;
  value: number;
  id: string;
  delta: number;
  entries: PerformanceEntry[];
  rating: "good" | "needs-improvement" | "poor";
}

// Thresholds for Core Web Vitals (Google's recommendations)
const THRESHOLDS = {
  LCP: { good: 2500, poor: 4000 },
  FID: { good: 100, poor: 300 },
  INP: { good: 200, poor: 500 },
  CLS: { good: 0.1, poor: 0.25 },
  FCP: { good: 1800, poor: 3000 },
  TTFB: { good: 800, poor: 1800 },
};

// Function to calculate rating based on our thresholds
function calculateRating(
  metricName: string,
  value: number
): "good" | "needs-improvement" | "poor" {
  const threshold = THRESHOLDS[metricName as keyof typeof THRESHOLDS];
  if (!threshold) return "good"; // Default for unknown metrics

  if (value <= threshold.good) return "good";
  if (value <= threshold.poor) return "needs-improvement";
  return "poor";
}

// Function to get threshold information for analytics
function getThresholdInfo(metricName: string) {
  const threshold = THRESHOLDS[metricName as keyof typeof THRESHOLDS];
  return threshold || null;
}

// Function to check if metric needs attention
function needsAttention(metricName: string, value: number): boolean {
  const threshold = THRESHOLDS[metricName as keyof typeof THRESHOLDS];
  if (!threshold) return false;
  return value > threshold.poor;
}

export function PerformanceMonitor() {
  useEffect(() => {
    // Load web-vitals dynamically to avoid blocking
    const loadWebVitals = async () => {
      try {
        const webVitals = await import("web-vitals");
        const { onCLS, onFID, onFCP, onLCP, onTTFB, onINP } = webVitals;

        const sendToAnalytics = (metric: Metric) => {
          // Calculate our own rating based on thresholds
          const customRating = calculateRating(metric.name, metric.value);
          const thresholdInfo = getThresholdInfo(metric.name);
          const requiresAttention = needsAttention(metric.name, metric.value);

          // Enhanced analytics payload with threshold information
          const analyticsData = {
            metric_name: metric.name,
            metric_value: metric.value,
            metric_rating: metric.rating, // Original web-vitals rating
            custom_rating: customRating, // Our threshold-based rating
            metric_id: metric.id,
            threshold_good: thresholdInfo?.good,
            threshold_poor: thresholdInfo?.poor,
            needs_attention: requiresAttention,
            performance_budget_exceeded: requiresAttention,
          };

          // Send to Google Analytics if available
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          if (typeof window !== "undefined" && (window as any).gtag) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (window as any).gtag("event", metric.name, {
              event_category: "Web Vitals",
              event_label: metric.id,
              value: Math.round(
                metric.name === "CLS" ? metric.value * 1000 : metric.value
              ),
              custom_parameter_rating: customRating,
              custom_parameter_needs_attention: requiresAttention,
              non_interaction: true,
            });
          }

          // Send to Vercel Analytics (if available)
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          if (typeof window !== "undefined" && (window as any).va) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (window as any).va("track", "Web Vitals", analyticsData);
          }

          // Enhanced console logging in development with threshold context
          if (process.env.NODE_ENV === "development") {
            const colors = {
              good: "color: green; font-weight: bold;",
              "needs-improvement": "color: orange; font-weight: bold;",
              poor: "color: red; font-weight: bold;",
            };

            console.group(
              `%c${metric.name}: ${metric.value} (${customRating})`,
              colors[customRating]
            );
            console.log(`Original Rating: ${metric.rating}`);
            console.log(`Custom Rating: ${customRating}`);
            if (thresholdInfo) {
              console.log(
                `Thresholds - Good: ≤${thresholdInfo.good}, Poor: >${thresholdInfo.poor}`
              );
            }
            if (requiresAttention) {
              console.warn(
                `⚠️ Performance budget exceeded! Consider optimization.`
              );
            }
            console.groupEnd();
          }

          // Alert for poor performance in production (optional)
          if (process.env.NODE_ENV === "production" && requiresAttention) {
            // Only log critical performance issues in production
            console.warn(
              `Performance Alert: ${metric.name} (${metric.value}) exceeds threshold`
            );
          }

          // Send to custom analytics endpoint if needed
          if (process.env.NEXT_PUBLIC_ANALYTICS_ENDPOINT) {
            fetch(process.env.NEXT_PUBLIC_ANALYTICS_ENDPOINT, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                type: "web-vital",
                ...analyticsData,
                url: window.location.href,
                userAgent: navigator.userAgent,
                timestamp: Date.now(),
              }),
            }).catch(() => {
              // Silently fail - analytics shouldn't break the app
            });
          }
        };

        // Monitor Core Web Vitals
        onCLS(sendToAnalytics);
        onFCP(sendToAnalytics);
        onLCP(sendToAnalytics);
        onTTFB(sendToAnalytics);

        // Monitor INP (preferred) and FID (fallback)
        onINP(sendToAnalytics);
        onFID(sendToAnalytics);
      } catch (error) {
        console.warn("Failed to load web-vitals:", error);
      }
    };

    // Load after a short delay to avoid blocking critical path
    const timer = setTimeout(loadWebVitals, 1000);

    return () => clearTimeout(timer);
  }, []);

  return null; // This component doesn't render anything
}

export default PerformanceMonitor;
